'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { BookOpen, Search, Calendar, User, Tag, ArrowRight, Sparkles, Clock } from 'lucide-react';
import { StrapiBlogPost, StrapiCategory, getStrapiMediaUrl } from '@/lib/api/strapi-client';

interface BlogClientViewProps {
  initialPosts: StrapiBlogPost[];
  categories: StrapiCategory[];
}

export function BlogClientView({ initialPosts, categories }: BlogClientViewProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const categoryNames = ['All', ...categories.map((c) => c.name)];

  const filteredPosts = initialPosts.filter((post) => {
    const categoryName = post.category?.name || 'Vedic Astrology';
    const matchesCategory = selectedCategory === 'All' || categoryName === selectedCategory;

    const query = searchQuery.toLowerCase().trim();
    const matchesQuery =
      !query ||
      post.title.toLowerCase().includes(query) ||
      post.excerpt.toLowerCase().includes(query) ||
      (post.body && post.body.toLowerCase().includes(query));

    return matchesCategory && matchesQuery;
  });

  return (
    <div className="space-y-10 sm:space-y-12 py-6 sm:py-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Hero / Knowledge Library Banner */}
      <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-4 relative overflow-hidden">
        <div className="flex items-center gap-2 text-[#5ab4ff] text-xs font-semibold uppercase tracking-wider font-mono">
          <BookOpen className="w-4 h-4 text-[#5ab4ff]" />
          <span>Vedic Knowledge Library</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-['Lexend'] font-medium text-white flex items-center gap-3">
          <span>Astrology Knowledge & Guide Base</span>
        </h1>

        <p className="text-xs sm:text-sm text-[#dcecff]/75 max-w-3xl leading-relaxed font-['Outfit']">
          Master classical Vedic astrology concepts including Nakshatras, Shadbala planetary strength, Ashtakavarga bindu grids, Dasha timelines, and mathematical Yantras.
        </p>

        {/* Search Input & Category Filter Pills */}
        <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <div className="relative w-full sm:w-80 shrink-0">
            <Search className="w-4 h-4 text-[#dcecff]/40 absolute left-4 top-3.5" />
            <input
              type="text"
              placeholder="Search knowledge topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 bg-white/[0.04] border border-white/12 rounded-xl pl-11 pr-4 text-xs text-white placeholder-[#dcecff]/40 focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff]"
              data-testid="blog-search-input"
            />
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap items-center gap-2" data-testid="category-pills">
            {categoryNames.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-white text-[#050a24] shadow-[0_2px_12px_rgba(255,255,255,0.2)]'
                    : 'bg-white/[0.04] text-[#dcecff]/70 border border-white/10 hover:text-white hover:border-white/20'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      {initialPosts.length === 0 ? (
        <div className="text-center p-12 rounded-3xl border border-white/16 bg-[rgba(5,10,36,0.92)] shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-3" data-testid="articles-unavailable">
          <Sparkles className="w-8 h-8 text-[#5ab4ff] mx-auto opacity-70" />
          <h3 className="font-['Lexend'] text-lg font-medium text-white">Articles are temporarily unavailable</h3>
          <p className="text-xs text-[#dcecff]/60 font-['Outfit']">We are currently updating our article feed. Please check back shortly.</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center p-12 rounded-3xl border border-white/16 bg-[rgba(5,10,36,0.92)] shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-3">
          <Sparkles className="w-8 h-8 text-[#5ab4ff] mx-auto opacity-70" />
          <h3 className="font-['Lexend'] text-lg font-medium text-white">No matching articles found</h3>
          <p className="text-xs text-[#dcecff]/60 font-['Outfit']">Try adjusting your search query or category filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-posts-grid">
          {filteredPosts.map((post) => {
            const imageUrl = getStrapiMediaUrl(post.featured_image?.url);
            const categoryName = post.category?.name || 'Vedic Astrology';
            const authorName = post.author?.name || 'AstroPredict Team';
            const readTime = post.read_time || '5 min read';

            const formattedDate = post.published_date
              ? new Date(post.published_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : post.publishedAt
              ? new Date(post.publishedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'Recent';

            return (
              <article
                key={post.id}
                data-testid="blog-card"
                className="group relative rounded-3xl border border-white/16 bg-[rgba(5,10,36,0.92)] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl hover:border-[#5ab4ff]/50 transition-all duration-300 flex flex-col justify-between"
              >
                <div className="p-6 space-y-4">
                  {/* Category Badge & Read Time Header */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[10px] uppercase font-bold px-2.5 py-0.5 rounded-full bg-[#5ab4ff]/15 border border-[#5ab4ff]/30 text-[#5ab4ff]">
                      {categoryName}
                    </span>
                    <span className="text-[11px] text-[#dcecff]/60 font-mono flex items-center gap-1">
                      <Clock className="w-3 h-3 text-[#dcecff]/50" />
                      {readTime}
                    </span>
                  </div>

                  {/* Optional Featured Image */}
                  {imageUrl && (
                    <div className="relative aspect-[16/9] w-full rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                      <Image src={imageUrl} alt={post.title} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}

                  {/* Title & Excerpt */}
                  <h2 className="text-lg font-['Lexend'] font-semibold text-white group-hover:text-[#5ab4ff] transition-colors leading-snug">
                    <Link href={`/blog/${post.slug}`} className="focus:outline-none">
                      <span className="absolute inset-0" />
                      {post.title}
                    </Link>
                  </h2>

                  <p className="text-xs text-[#dcecff]/75 line-clamp-3 leading-relaxed font-['Outfit']">
                    {post.excerpt}
                  </p>
                </div>

                {/* Footer: Author Byline & Date */}
                <div className="p-6 pt-0 border-t border-white/10 mt-2 flex items-center justify-between text-[11px] text-[#dcecff]/60 font-['Outfit']">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-[#5ab4ff]" />
                    <span className="line-clamp-1 font-medium text-white">{authorName}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[#5ab4ff] font-semibold group-hover:translate-x-0.5 transition-transform shrink-0">
                    <span>Read Guide</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
